import {assert} from 'Asserts';
import {canvasVersionURL} from 'canvas-core/CanvasURL';
import {NotePart, NotePartType} from 'notebook/util/NoteUtilConstants';

const CANVAS_DEFINITION_ID_PATTERN = 'cnvsdef_[a-zA-Z0-9]{8,16}';
const CANVAS_VERSION_ID_PATTERN = 'cnvsver_[a-zA-Z0-9]{8,16}';
const CANVAS_DEFINITION_ID_REGEX = new RegExp(`^${CANVAS_DEFINITION_ID_PATTERN}$`);
const CANVAS_VERSION_ID_REGEX = new RegExp(`^${CANVAS_VERSION_ID_PATTERN}$`);
const CANVAS_TAG_REGEX = /<bnch_canvas>\s*(\{[\s\S]*?\})\s*<\/bnch_canvas>/g;
const CANVAS_MARKER_PREFIX = 'BNCH_CANVAS_MARKER_';
const CANVAS_LINK_TEXT = 'Canvas';

export interface CanvasRef {
  canvasDefinitionId: string;
  canvasVersionId: string;
}

/**
 * Validates and parses a bnch_canvas tag's JSON content.
 * Returns the parsed data if valid, null if invalid.
 */
export function parseBnchCanvasJSON(
  content: string
): {canvasDefinitionId: string; canvasVersionId: string} | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch (error) {
    if (error instanceof SyntaxError) {
      return null;
    }
    throw error;
  }

  if (typeof parsed !== 'object' || parsed == null || Array.isArray(parsed)) {
    return null;
  }

  const parsedObj = parsed as Record<string, unknown>;
  const {canvasDefinitionId, canvasVersionId} = parsedObj;
  if (
    typeof canvasDefinitionId !== 'string' ||
    !CANVAS_DEFINITION_ID_REGEX.test(canvasDefinitionId) ||
    typeof canvasVersionId !== 'string' ||
    !CANVAS_VERSION_ID_REGEX.test(canvasVersionId)
  ) {
    return null;
  }

  return {canvasDefinitionId, canvasVersionId};
}

export function canvasRefKey(canvas: CanvasRef): string {
  return `${canvas.canvasDefinitionId}:${canvas.canvasVersionId}`;
}

export function extractAndReplaceBnchCanvasTags(markdown: string): string {
  return markdown.replace(CANVAS_TAG_REGEX, (_match, json: string) => {
    const parsed = parseBnchCanvasJSON(json);
    if (parsed == null) {
      return '';
    }
    return `\n\n${CANVAS_MARKER_PREFIX}${parsed.canvasDefinitionId}_${parsed.canvasVersionId}\n\n`;
  });
}

export function replaceBnchCanvasMarkersInNoteParts(noteParts: Array<NotePart>): Array<NotePart> {
  const markerRegex = new RegExp(
    `^\\s*${CANVAS_MARKER_PREFIX}(${CANVAS_DEFINITION_ID_PATTERN})_(${CANVAS_VERSION_ID_PATTERN})\\s*$`
  );
  return noteParts.flatMap((notePart) => {
    if (notePart.type !== NotePartType.TEXT || !notePart.text) {
      return [notePart];
    }
    const match = notePart.text.match(markerRegex);
    if (match) {
      const url = canvasVersionURL(match[1], match[2]);
      const textNotePart = notePart;
      assert(textNotePart.type === NotePartType.TEXT, `Expected text note part, found ${textNotePart.type}`);
      return [
        {
          ...textNotePart,
          text: CANVAS_LINK_TEXT,
          formats: {
            ...textNotePart.formats,
            link: [{start: 0, end: CANVAS_LINK_TEXT.length, data: {url}}],
          },
        } as NotePart,
      ];
    }
    return [notePart];
  });
}
